import React from 'react';
import { useTranslation } from 'react-i18next';
import usePublicStats from '../../hooks/usePublicStats';

/**
 * Hero — mirrors landing-static_20260420/veripass-landing-en-{dark,light}.html
 * Two-column hero: FDA·MoCRA badge headline on the left, a browser-chrome
 * mockup showing a scrollable static sample compliance report on the right.
 */
const Hero = () => {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  // Q7 — one shared reader for the public corpus figures. See usePublicStats.
  const { warningLetters: wlCount, totalRecords, fmt } = usePublicStats();

  const t = isKo
    ? {
        badge: 'FDA · MoCRA 컴플라이언스 검증',
        h1a: '미국 시장에',
        h1b: '마케팅 문구가',
        h1c: '통할까요?',
        sub1: 'FDA·MoCRA 기반 AI가 즉시 판정합니다.',
        sub2: '위반 문구에는 즉시 대체 문구를 제시합니다.',
        stat1Label: '평균 검증 시간',
        stat2Label: 'FDA Warning Letters',
        stat3Label: 'FDA 학습 데이터',
        ctaPrimary: '무료로 검증하기',
        ctaSecondary: '데모 보기',
        reportLabel: '리포트',
        score: '컴플라이언스 점수',
        productName: 'UV Defense Complex SPF50',
        highRisk: '고위험 — 즉시 수정 필요',
        highRiskDesc: '이 문구는 FTC 입증 기준과 충돌할 수 있습니다.',
        analysis: '원문 문구 분석',
        quoteMid: ' collagen production to ',
        quoteTail: ' deep wrinkles overnight.',
        answers: '입력한 답변',
        qClinical: '임상 근거 자료 보유 여부?',
        aNo: '아니오',
        prescription: '처방',
        original: '원문',
        saferWording: '더 안전한 표현',
        action: '적용',
        apply: '적용',
        recommendedClaims: '권장 클레임',
        recClaimText: '지시에 따라 사용할 경우 잔주름 개선에 도움이 될 수 있습니다.',
        applyToMarketing: '마케팅 문구에 적용',
        drugClaim: '의약품 클레임',
        drugClaimDesc: '구조·기능 표현은 OTC/의약품 분류로 전환될 수 있습니다.',
        mocra: 'MoCRA',
        mocraDesc: '화장품에도 라벨링 및 중대 이상사례 보고 규정이 적용됩니다.',
        tip: '팁: 미국 출시 전, 촉진·제거 동사를 외관 변화 중심 표현으로 바꾸세요.',
        analyzed: '분석 근거',
        analyzedRow1: '컴플라이언스 체크 — 전체 문구',
        analyzedRow2: 'FDA 화장품 규정 대조',
        analyzedRow3: '과학적 근거',
        scrollHint: '↓ 프레임 내부를 스크롤하여 전체 샘플 리포트를 확인하세요',
      }
    : {
        badge: 'FDA · MoCRA compliance validation',
        h1a: 'Can your',
        h1b: 'marketing claims',
        h1c: 'work in the U.S.?',
        sub1: 'FDA·MoCRA-powered AI decides instantly.',
        sub2: 'Violations get instant fix guidance.',
        stat1Label: 'Avg. validation time',
        stat2Label: 'FDA Warning Letters',
        stat3Label: 'FDA training records',
        ctaPrimary: 'Validate free now',
        ctaSecondary: 'View demo',
        reportLabel: 'Report',
        score: 'Compliance score',
        productName: 'UV Defense Complex SPF50',
        highRisk: 'HIGH RISK — Immediate revision needed',
        highRiskDesc: 'This copy may conflict with FTC substantiation expectations.',
        analysis: 'Original Copy Analysis',
        quoteMid: ' collagen production to ',
        quoteTail: ' deep wrinkles overnight.',
        answers: 'Your answers',
        qClinical: 'Clinical evidence on file?',
        aNo: 'No',
        prescription: 'Prescription',
        original: 'Original',
        saferWording: 'Safer wording',
        action: 'Action',
        apply: 'Apply',
        recommendedClaims: 'Recommended claims',
        recClaimText: 'Helps improve the appearance of fine lines when used as directed.',
        applyToMarketing: 'Apply to marketing copy',
        drugClaim: 'Drug claim',
        drugClaimDesc: 'Structure/function language may trigger OTC/drug pathways.',
        mocra: 'MoCRA',
        mocraDesc: 'Labeling and serious adverse event rules still apply to cosmetics.',
        tip: 'Tip: swap stimulation/removal verbs for appearance-only language before U.S. launch.',
        analyzed: 'How we analyzed this',
        analyzedRow1: 'Compliance check — full text',
        analyzedRow2: 'FDA vs. cosmetics regulations',
        analyzedRow3: 'Scientific evidence',
        scrollHint: '↓ Scroll inside the frame to see the full sample report',
      };

  const validationTime = isKo ? '1초 이내' : 'Under 1 sec';

  // Q7 (Claim-valid-error-fix-8-19) — BOTH figures now come from the server.
  //
  // `/api/v1/public/stats` returns `warning_letters_count` and `total_records`,
  // counted from the shipped corpus files (synthetic seed rows excluded), so the
  // numbers are true by construction and cannot drift from the data.
  //
  // Until that landed, neither key existed on the response: these branches were
  // DEAD and the hardcoded fallbacks — '3,005' and the earlier '12,690+' —
  // rendered unconditionally in production as figures with nothing behind them.
  // The real counts are an order of magnitude apart from '3,005'.
  //
  // ⚠️ If a count is unavailable the fallback MUST stay non-numeric. Never put a
  // literal back here: an unreachable branch is exactly how the last two
  // fabricated numbers survived so long.
  const warningLetters = wlCount != null
    ? fmt(wlCount)
    : (isKo ? '지속 수집' : 'Continuously collected');
  const trainingRecords = totalRecords != null
    ? `${fmt(totalRecords)}+`
    : (isKo ? '지속 업데이트' : 'Continuously updated');

  return (
    <section className="hero hero-v2">
      <div className="hero-glow hero-glow-tr" aria-hidden="true" />
      <div className="hero-glow hero-glow-bl" aria-hidden="true" />
      <div className="wrap hero-v2-inner">
        <div className="hero-v2-left">
          <div className="hero-v2-badge">{t.badge}</div>
          <h1 className="hero-v2-h1">
            {t.h1a}<br />{t.h1b}<br />{t.h1c}
          </h1>
          <p className="hero-v2-sub">{t.sub1}</p>
          <p className="hero-v2-sub">{t.sub2}</p>

          <div className="hero-v2-stats">
            <div className="hero-v2-stat">
              <span className="hero-v2-stat-num">{validationTime}</span>
              <span className="hero-v2-stat-lbl">{t.stat1Label}</span>
            </div>
            <span className="hero-v2-stat-sep" aria-hidden="true" />
            <div className="hero-v2-stat">
              <span className="hero-v2-stat-num">{warningLetters}</span>
              <span className="hero-v2-stat-lbl">{t.stat2Label}</span>
            </div>
            <span className="hero-v2-stat-sep" aria-hidden="true" />
            <div className="hero-v2-stat">
              <span className="hero-v2-stat-num">{trainingRecords}</span>
              <span className="hero-v2-stat-lbl">{t.stat3Label}</span>
            </div>
          </div>

          <div className="hero-v2-ctas">
            <a className="hero-v2-primary" href="/login?signup=true">{t.ctaPrimary}</a>
            <a className="hero-v2-link" href="#features">{t.ctaSecondary}</a>
          </div>
        </div>

        <div className="hero-v2-right">
          <div className="hero-mockup" role="presentation">
            <div className="hero-mockup-chrome">
              <div className="hero-mockup-dots" aria-hidden="true">
                <span style={{ background: '#FF5F57' }} />
                <span style={{ background: '#FEBC2E' }} />
                <span style={{ background: '#28C840' }} />
              </div>
              <div className="hero-mockup-url">veripass.kr/reports/VP-20260414-0023</div>
            </div>

            <div className="hero-mockup-body">
              <div
                className="hero-mockup-scroll"
                role="region"
                aria-label={t.scrollHint}
                tabIndex={0}
              >
                <div className="hr-head">
                  <div>
                    <p className="hr-eyebrow">{t.reportLabel} · VP-20260414-0023</p>
                    <p className="hr-title">{t.productName}</p>
                  </div>
                  <div className="hr-score">
                    <p className="hr-score-num">53<span className="hr-score-denom"> / 100</span></p>
                    <p className="hr-score-label">{t.score}</p>
                  </div>
                </div>

                <div className="hr-risk">
                  <p className="hr-risk-title">{t.highRisk}</p>
                  <p className="hr-risk-desc">{t.highRiskDesc}</p>
                </div>

                <div className="hr-block">
                  <p className="hr-block-label">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#a100aa', marginRight: 6, verticalAlign: '-3px' }}>
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                      <path d="m9 15 2 2 4-4" />
                    </svg>
                    {t.analysis}
                  </p>
                  <div className="hr-quote">
                    <span className="hr-flag">Stimulates</span>{t.quoteMid}
                    <span className="hr-flag">eliminate</span>{t.quoteTail}
                  </div>
                </div>

                <div className="hr-answers">
                  <p className="hr-answers-label">{t.answers}</p>
                  <p className="hr-answers-q">
                    {t.qClinical} <span className="hr-answers-a">{t.aNo}</span>
                  </p>
                </div>

                <div className="hr-block">
                  <p className="hr-block-label">{t.prescription}</p>
                  <div className="hr-rx">
                    <div className="hr-rx-head">
                      <span>{t.original}</span>
                      <span>{t.saferWording}</span>
                      <span className="hr-rx-action-head">{t.action}</span>
                    </div>
                    <div className="hr-rx-row">
                      <span className="hr-rx-orig">stimulates</span>
                      <span className="hr-rx-safe">supports</span>
                      <button type="button" className="hr-rx-apply" tabIndex={-1}>{t.apply}</button>
                    </div>
                    <div className="hr-rx-row hr-rx-row-last">
                      <span className="hr-rx-orig">eliminate</span>
                      <span className="hr-rx-safe">reduces the look of</span>
                      <button type="button" className="hr-rx-apply" tabIndex={-1}>{t.apply}</button>
                    </div>
                  </div>
                </div>

                <div className="hr-block">
                  <p className="hr-block-label">{t.recommendedClaims}</p>
                  <div className="hr-rec">
                    <p className="hr-rec-text">{t.recClaimText}</p>
                    <button type="button" className="hr-rec-apply" tabIndex={-1}>{t.applyToMarketing}</button>
                  </div>
                </div>

                <div className="hr-grid2">
                  <div className="hr-chip">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#f87171', display: 'block', marginBottom: 6 }}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" x2="12" y1="8" y2="12" />
                      <line x1="12" x2="12.01" y1="16" y2="16" />
                    </svg>
                    <p className="hr-chip-title hr-chip-title-red">{t.drugClaim}</p>
                    <p className="hr-chip-desc">{t.drugClaimDesc}</p>
                  </div>
                  <div className="hr-chip">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#d97706', display: 'block', marginBottom: 6 }}>
                      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                    </svg>
                    <p className="hr-chip-title hr-chip-title-amber">{t.mocra}</p>
                    <p className="hr-chip-desc">{t.mocraDesc}</p>
                  </div>
                </div>

                <div className="hr-tip">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#d050d8', flexShrink: 0, marginTop: 2 }}>
                    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                    <path d="M9 18h6" />
                    <path d="M10 22h4" />
                  </svg>
                  <p className="hr-tip-text">{t.tip}</p>
                </div>

                <div className="hr-analyzed">
                  <p className="hr-analyzed-label">{t.analyzed}</p>
                  <div className="hr-analyzed-row"><span>{t.analyzedRow1}</span><span className="hr-play">▶</span></div>
                  <div className="hr-analyzed-row"><span>{t.analyzedRow2}</span><span className="hr-play">▶</span></div>
                  <div className="hr-analyzed-row"><span>{t.analyzedRow3}</span><span className="hr-play">▶</span></div>
                </div>
              </div>

              <div className="hero-mockup-fade" aria-hidden="true" />
              <p className="hero-mockup-hint">{t.scrollHint}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
